import { LegendDTO, TemplateDTO } from './dtos/legend'
import { Model } from '../infra/model'
import once from 'lodash/once'
import { computed, observable, makeObservable } from 'mobx'
import { PrimeWorkspace } from '../iam/WorkspaceStore'
import { generateAvatarFallback } from '../utils/image-util'
import Workspace from '../iam/models/Workspace'
import { Id } from '../types/definitions'
import { DateZero, maybeParseDate } from '../utils/date-util'

/**
 * Legend version ID.
 */
export interface LegendVersionId {
  readonly id: string
  readonly version: number
}

/**
 * Creates a Legend Version ID.
 *
 * @param id
 * @param version
 */
export function LegendVersionId(id: string, version: number): LegendVersionId {
  return {
    id,
    version,
  }
}

/**
 * Whether 2 Legend Version IDs are considered the same.
 */
LegendVersionId.equals = function equals(
  left: LegendVersionId,
  right: LegendVersionId,
) {
  return (
    left === right || (left.id === right.id && left.version === right.version)
  )
}

/**
 * Asserts the Legend Version ID is non-empty.
 */
LegendVersionId.assert = function assert(
  input: LegendVersionId | undefined | null,
): LegendVersionId {
  if (
    LegendVersionId.equals(input || LegendVersionId.none, LegendVersionId.none)
  ) {
    throw new TypeError(
      'A non-empty Legend Version ID was expected, but was empty.',
    )
  }
  return input!
}

/**
 * Formats the Legend Version ID as a string.
 *
 * @param legendVersionId
 */
LegendVersionId.asString = function asString(legendVersionId: LegendVersionId) {
  return `${legendVersionId.id}:${legendVersionId.version}`
}

/**
 * No ID.
 */
LegendVersionId.none = {
  id: 'none',
  version: -1,
} as LegendVersionId

/**
 * A Legend that has been Published and is ready to be used for new jobs.
 * Contains only props used for Job-related purposes.
 */
export class PublishedLegend extends Model<LegendDTO, PublishedLegend> {
  @observable private _imageUrl?: string = undefined

  id = Id.none
  @observable version = -1
  @observable name = 'Legend'
  @observable description = ''
  @observable workspace = Workspace.none
  @observable viewOrder = 1000
  @observable dateCreated = DateZero()
  @observable inactive = false

  // The following props are only available on a full Legend.
  @observable allowScheduling = false
  @observable allowProSelection = false
  @observable skipBilling = false
  @observable skipQuote = false
  @observable showAvailabilityCount = false
  @observable priceUnit = ''
  @observable templates: TemplateDTO[] = []

  static get none() {
    return lazyNone()
  }

  constructor(private primeWorkspace: PrimeWorkspace) {
    super()
    makeObservable(this)
  }

  /**
   * Legend version ID.
   */
  @computed
  get versionId(): LegendVersionId {
    if (!this.id) {
      return LegendVersionId.none
    }

    return {
      id: Id.assert(this.id),
      version: this.version,
    }
  }

  /**
   * Image URL to use if any.
   */
  @computed
  get imageUrl() {
    return (
      this._imageUrl ||
      generateAvatarFallback(this.name, this.workspace.branding.colors.primary)
    )
  }

  /**
   * Parse a DTO.
   * @param attrs
   */
  parse(attrs: Partial<LegendDTO>): Partial<PublishedLegend> {
    // This is private so TS isn't letting us return it in an object below. Sad.
    this._imageUrl = attrs.image_url || this._imageUrl
    return {
      id: attrs.id,
      viewOrder: attrs.view_order,
      workspace: this.primeWorkspace({ id: attrs.workspace_id }),
      version: attrs.version,
      name: attrs.name,
      inactive: attrs.inactive,
      description: attrs.description,
      dateCreated: maybeParseDate(attrs.date_created),
      allowScheduling: attrs.allowScheduling,
      allowProSelection: attrs.allowProSelection,
      skipBilling: attrs.skipBilling,
      skipQuote: attrs.skipQuote,
      priceUnit: attrs.priceUnit,
      showAvailabilityCount: attrs.showAvailabilityCount,
      templates: attrs.templates,
    }
  }
}

const lazyNone = once(() =>
  new PublishedLegend(() => Workspace.none).set({ name: 'Legend' }),
)
